import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FEEDBACK_FORM_URL } from '../config';

function HelpPage() {
  const navigate = useNavigate();

  const sectionCard = {
    backgroundColor: 'var(--surface-card)',
    borderRadius: '16px',
    border: '1px solid var(--border-subtle)',
    boxShadow: '0 1px 3px rgba(32,32,32,0.06)',
    padding: '1.5rem',
    marginBottom: '1rem',
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--surface-bg)', padding: '2rem 1rem', fontFamily: 'var(--font)' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
          <button
            onClick={() => navigate('/dashboard')}
            aria-label="Back to dashboard"
            title="Back to dashboard"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              flexShrink: 0,
              backgroundColor: 'transparent',
              color: 'var(--maroon)',
              border: '1px solid var(--maroon-border)',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease, border-color 0.2s ease',
            }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'var(--maroon-light)'; e.currentTarget.style.borderColor = 'var(--maroon)'; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'var(--maroon-border)'; }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <h1 style={{ margin: 0, color: 'var(--maroon)', fontSize: '1.8rem' }}>UTrade Help Center</h1>
        </div>

        <p style={{ color: 'var(--text-muted)', marginBottom: '1.2rem' }}>
          UTrade is a UMass-only marketplace for buying, selling, and giving away campus items.
          We simply connect students — <strong>you arrange the meetup and payment yourselves</strong>
          {' '}(Venmo, Zelle, cash). UTrade does not handle money or delivery, and there are no fees.
        </p>

        <div style={sectionCard}>
          <h2 style={{ marginTop: 0, color: 'var(--maroon)', fontSize: '1.1rem' }}>How Buying Works</h2>
          <ol style={{ margin: '0.5rem 0 0 1rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
            <li>Find an item and tap <strong>Request Item</strong> (add a short message if you like).</li>
            <li>The seller reviews your request and accepts or passes — you'll get an email either way.</li>
            <li>If accepted, the item is <strong>reserved for you</strong> and you receive a <strong>6-digit PIN</strong> (by email and in Your Dashboard).</li>
            <li>Message the seller to pick a public spot on campus, whenever suits you both.</li>
            <li>Meet up, check the item over, and pay the seller directly.</li>
            <li>Show the seller your PIN — they enter it to confirm the handoff. Done!</li>
          </ol>
          <p style={{ margin: '0.75rem 0 0 0', color: 'var(--text-soft)', fontSize: '0.88rem' }}>
            No rush — if a pickup doesn't happen within about a week, the reservation just expires and
            the item goes back up for everyone. You can always request it again.
          </p>
        </div>

        <div style={sectionCard}>
          <h2 style={{ marginTop: 0, color: 'var(--maroon)', fontSize: '1.1rem' }}>How Selling Works</h2>
          <ol style={{ margin: '0.5rem 0 0 1rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
            <li>Tap <strong>Sell</strong> and create a listing with clear photos and details.</li>
            <li>When a request comes in, review it and <strong>Accept</strong> or <strong>Decline</strong>.</li>
            <li>Accepting reserves the item for that buyer and sends them a PIN.</li>
            <li>Coordinate a public campus meetup in chat, at a time that works for you.</li>
            <li>Meet, hand over the item, and take payment directly.</li>
            <li>Ask the buyer for their <strong>6-digit PIN</strong> and enter it in Your Dashboard to complete the sale.</li>
          </ol>
          <p style={{ margin: '0.75rem 0 0 0', color: 'var(--text-soft)', fontSize: '0.88rem' }}>
            No pressure — if it doesn't happen within about a week, the reservation expires and your
            listing becomes available again for everyone automatically.
          </p>
        </div>

        <div style={sectionCard}>
          <h2 style={{ marginTop: 0, color: 'var(--maroon)', fontSize: '1.1rem' }}>The 6-Digit PIN</h2>
          <p style={{ margin: '0.25rem 0 0 0', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            The PIN is just a simple way to confirm the item changed hands — it is <strong>not a payment</strong> and not a password.
          </p>
          <ul style={{ margin: '0.6rem 0 0 1rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            <li>The <strong>buyer</strong> gets the PIN when a request is accepted (email + Your Dashboard).</li>
            <li>The <strong>seller</strong> enters it at the meetup to mark the deal complete.</li>
            <li>Only share the PIN once you're together and happy with the item — entering it records the sale for both of you.</li>
          </ul>
        </div>

        <div style={sectionCard}>
          <h2 style={{ marginTop: 0, color: 'var(--maroon)', fontSize: '1.1rem' }}>Safety Tips</h2>
          <ul style={{ margin: '0.5rem 0 0 1rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            <li>Meet in public campus locations.</li>
            <li>Do not share sensitive personal information.</li>
            <li>Inspect items before paying.</li>
            <li>If something feels suspicious, stop and report it.</li>
          </ul>
        </div>

        <div style={sectionCard}>
          <h2 style={{ marginTop: 0, color: 'var(--maroon)', fontSize: '1.1rem' }}>Contact & Feedback</h2>
          <p style={{ margin: '0 0 0.6rem 0', color: 'var(--text-muted)' }}>
            Need help? Reach out at <strong>hchhabra@umass.edu</strong>.
          </p>
          <p style={{ margin: '0 0 0.6rem 0', color: 'var(--text-muted)' }}>
            Advertising &amp; partnerships — promoting a club, event, or item? Email{' '}
            <a href="mailto:hchhabra@umass.edu?subject=UTrade%20Advertising%20%26%20Partnerships" style={{ color: 'var(--maroon)', fontWeight: 600 }}>hchhabra@umass.edu</a>.
          </p>
          <button
            onClick={() => window.open(FEEDBACK_FORM_URL, '_blank', 'noopener,noreferrer')}
            style={{
              padding: '0.65rem 1.2rem',
              backgroundColor: 'var(--maroon)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: 600,
              fontFamily: 'var(--font-heading)',
              fontSize: '0.9rem',
            }}
          >
            Give Feedback
          </button>
        </div>
      </div>
    </div>
  );
}

export default HelpPage;
